<template>
  <div class="p2a-action-form p2a-relationship-form">
    <field-select
      id="input-title"
      :label="'How would you rate your relationship?'"
      :required="true"
      :default-empty-option="true"
      :name="'relationship_rating'"
      :options="ratingOptions"
      :default-empty-label="'Select rating'"
      :value="rating"
      :error="validationErrors.rating"
      autocomplete-props="honorific-prefix"
      @change.native="keyUp('rating', $event)"
    />

    <field-select
      :label="'How do you know them?'"
      :required="true"
      :default-empty-option="true"
      :name="'relationship_hdyk'"
      :options="tagOptions"
      :default-empty-label="'This person is a...'"
      :value="hdyk"
      :error="validationErrors.hdyk"
      autocomplete-props="honorific-prefix"
      @change.native="keyUp('hdyk', $event)"
    />

    <field-text
      :label="'Please tell us more about your relationship.'"
      :required="true"
      :name="'relationship_details'"
      :placeholder="'I met them at...'"
      :type="'textarea'"
      :value="details"
      :error="validationErrors.details"
      @input="keyUp('details', $event)"
    />

    <primary-button
      class="btn--md"
      @click.native="openWillingToCall"
    >
      Submit Relationship
    </primary-button>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { debounce as _debounce, isEmpty as _isEmpty } from "lodash";
import FieldSelect from "WhoDoYouKnowComponents/fields/FieldSelect.vue";
import FieldText from "WhoDoYouKnowComponents/fields/FieldText.vue";
import PrimaryButton from "WhoDoYouKnowComponents/buttons/PrimaryButton.vue";

export default {
  name: "RelationshipForm",
  components: {
    FieldSelect,
    PrimaryButton,
    FieldText
  },
  props: {
    id: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      rating: null,
      hdyk: null,
      details: null,
      willingToCall: false,
      validationErrors: {},
      validationMessages: {
        rating: "Please select a rating",
        hdyk: "Please select a type of relationship",
        details: "Please enter details about your relationship"
      }
    };
  },
  computed: {
    ...mapState("campaign", {
      ratings: state => state.relationshipRatings,
      tags: state => state.relationshipTags
    }),
    ...mapState("wdyk", {
      relationships: state => state.relationships
    }),
    isFormValid() {
      return this.validateForm();
    },
    tagOptions() {
      return this.tags.map(tag => ({ label: tag.tagName, value: tag.id }));
    },
    ratingOptions() {
      return this.ratings.map(rating => ({
        label: `${rating.rating} - ${rating.label}`,
        value: rating.rating
      }));
    }
  },
  inject: {
    stakeholderService: { from: "stakeholderService" },
    eventsBus: { from: "eventsBus" },
    axiosHelper: { from: "axiosHelper" }
  },
  mounted() {
    this.eventsBus.$on("clearRelationshipForm", () => {
      this.rating = null;
      this.hdyk = null;
      this.details = null;
    });

    this.eventsBus.$on("clickWillingToCall", $event => {
      this.willingToCall = $event.target.value;
      this.submit();
    });
  },
  methods: {
    ...mapActions({
      setLoading: "wdyk/loading"
    }),
    debounceValidateField: _debounce(function(field, value) {
      this.validateField(field, value);
    }, 800),
    keyUp(field, $event) {
      this[field] = $event.target.value;
      this.debounceValidateField(field, $event.target.value);
    },
    openWillingToCall() {
      if (this.isFormValid) {
        this.eventsBus.$emit("showWillingToCall");
      }
    },
    async submit() {
      if (!this.validateForm()) {
        return false;
      }

      this.eventsBus.$emit("hideWillingToCall");
      try {
        this.setLoading(true);
        await this.stakeholderService.createRelationship(
          this.id,
          this.rating,
          this.hdyk,
          this.details,
          this.willingToCall
        );
      } catch (error) {
        this.setLoading(false);
        const errorCode = this.axiosHelper.getErrorStatusCode(error);
        const errorMessage = this.axiosHelper.retrieveErrorMessage(error);

        // TODO: error messaging
        console.error(`An error occurred: (${errorCode}) ${errorMessage}`);
      }
      this.setLoading(false);
      return true;
    },
    validateField(field, value) {
      const isValid = _isEmpty(value);

      this.$set(
        this.validationErrors,
        field,
        isValid ? this.validationMessages[field] : null
      );

      return !isValid;
    },
    validateForm() {
      const fields = ["rating", "hdyk", "details"];
      let formValid = true;

      fields.forEach(field => {
        if (!this.validateField(field, this[field])) {
          formValid = false;
        }
      });

      return formValid;
    }
  }
};
</script>
